/**
 * <bigens-arrival> (T45, AUD3 #9): an estimated arrival range in the shopper's language and date format.
 * Markup from snippets/bigens-arrival.liquid: data-min / data-max (business days, Monday to Friday, from the theme
 * settings) and data-locale. The server prints an English fallback; this element rewrites it with
 * Intl.DateTimeFormat#formatRange ("Oct 14 – 20", "14.–20. Okt.", "14–20 okt.") and again whenever a re-render
 * (the cart drawer's morph) puts the fallback back. The ISO dates stay on data-start / data-end.
 */

/**
 * Today plus n business days (Saturday and Sunday don't count), at noon local time.
 * @param {number} days
 */
export function addBusinessDays(days) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
}

/**
 * "Oct 14 – 20" in the given locale (month short, day numeric).
 * @param {number} min
 * @param {number} max
 * @param {string | undefined} locale
 */
export function arrivalRange(min, max, locale) {
  const start = addBusinessDays(min);
  const end = addBusinessDays(Math.max(min, max));
  const options = /** @type {Intl.DateTimeFormatOptions} */ ({ month: 'short', day: 'numeric' });
  let format;
  try {
    format = new Intl.DateTimeFormat(locale, options);
  } catch {
    format = new Intl.DateTimeFormat(undefined, options);
  }
  const text =
    start.getTime() === end.getTime() || typeof format.formatRange !== 'function'
      ? start.getTime() === end.getTime()
        ? format.format(start)
        : `${format.format(start)} – ${format.format(end)}`
      : format.formatRange(start, end);
  return { start, end, text };
}

/**
 * One date, month short + day numeric, in the given locale.
 * @param {Date} date
 * @param {string | undefined} locale
 */
function formatDate(date, locale) {
  const options = /** @type {Intl.DateTimeFormatOptions} */ ({ month: 'short', day: 'numeric' });
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(date);
  }
}

/** @param {Date} date */
function isoDate(date) {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
}

class BigensArrival extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#render();
    this.#observer = new MutationObserver(() => {
      if (!this.querySelector('[data-arrival-text]')) this.#render();
    });
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #render() {
    const min = Number.parseInt(this.dataset.min || '5', 10);
    const max = Number.parseInt(this.dataset.max || String(min), 10);
    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    const { start, end, text } = arrivalRange(min, max, locale);
    const span = document.createElement('span');
    span.dataset.arrivalText = '';
    // data-mode="end": only the latest date ("Nov 11") for "delivery by {date}" (blocks/bigens-pdp-stock).
    span.textContent = this.dataset.mode === 'end' ? formatDate(end, locale) : text;
    this.dataset.start = isoDate(start);
    this.dataset.end = isoDate(end);
    this.#observer?.disconnect();
    this.replaceChildren(span);
    this.#observer?.observe(this, { childList: true, subtree: true, characterData: true });
  }
}

if (!customElements.get('bigens-arrival')) {
  customElements.define('bigens-arrival', BigensArrival);
}
